import BaseStore from 'stores/BaseStore';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStoreT } from 'types/frontend/RootStore';
import UserStore from 'stores/root/UserStore';
import {
  PersonalMessageParagraphT,
  PostReviewStatusT,
  PostT,
  PostTypeT,
} from 'types/shared/core/Post';
import PostActionStore from 'stores/PostActionStore';

import Co from 'constants/frontend/shared/Co';
import {
  DO_UPDATE_POST_REVIEW_STATUS,
  READ_POST_CONTENT,
} from 'network/shared/post';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import {
  EmptyRequestT,
  NewStatusRequestT,
} from 'types/shared/reqAndRes/CommonRequest';
import { api } from 'utils/api/api';
import interfaceBridge from 'utils/interfaceBridge';
import { PostReviewStatusUpdateResponseT } from 'types/shared/reqAndRes/Post';
import { ErrorCode } from 'constants/shared/ErrorCode';
import { MyError } from 'utils/MyError';
import { navigate } from 'routers/MyNavigator';
import StorageHelper from 'utils/StorageHelper';

export default class TeacherPostReviewDetailStore implements BaseStore {
  scope: 'mine' | 'inbox' = 'mine';
  postType!: PostTypeT;
  postId = 0;
  post: PostT | null = null;

  postActionStore: PostActionStore;

  userStore: UserStore;

  constructor(rootStore: RootStoreT) {
    this.userStore = rootStore.userStore;
    this.postActionStore = new PostActionStore(
      rootStore.userStore,
      this.editPostCallback,
      this.togglePostVisibilityCallback,
      this.deletePostCallback,
    );

    makeAutoObservable(this);
  }

  handlePostCornerAction = (post: PostT) => {
    if (this.postType === 'brandPost') {
      return;
    }
    this.postActionStore.handlePostCornerAction(post);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editPostCallback = (post: PostT) => {
    this.loadPost();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  togglePostVisibilityCallback = (post: PostT) => {
    // nothing to do
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deletePostCallback = (post: PostT) => {
    this.goToList();
  };

  goToList = () => {
    interfaceBridge.backAfterEditing();
  };

  init = async (
    scope: 'inbox' | 'mine',
    postType: PostTypeT,
    postId: number,
  ) => {
    this.scope = scope;
    this.postType = postType;
    this.postId = postId;

    console.log(`postType=${this.postType} , postId = ${this.postId}`);

    console.log('init');

    // 글 내용 하나 읽어오기
    await this.loadPost();
  };

  loadPost = async () => {
    try {
      const result = await api<EmptyRequestT, MyResponse<PostT>>(
        READ_POST_CONTENT(0, this.postType, this.postId),
        {},
      );

      console.log('post content result', result);

      if (result.isSuccess && result.object) {
        runInAction(() => {
          console.log('post result', result.object);
          this.post = result.object as PostT;
          this.makeTagNames();
        });
      } else if (!result.isSuccess) {
        await interfaceBridge.customAlert(
          '게시글 조회 실패',
          `오류가 발생했습니다. (${result.errorCode})`,
          '닫기',
        );
      }
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
  };

  makeTagNames = () => {
    // tag중 학년 테그의 한글 이름을 설정한다.
    // 클래스명은 서버에서 이미 세팅됨
    this.post?.postTags?.forEach((tag) => {
      if (tag.tagType === 'grade') {
        tag.tagName = Co.Academy.gradeNames.get(tag.tagValue);
      }
    });
  };

  handlePersonalMessageClick = (paragraph: PersonalMessageParagraphT) => {
    const tempDataKey = StorageHelper.saveTempData({
      targetStudents: this.post?.studentList || [],
      personalMessageContent: {
        baseText: paragraph.baseText,
        editedMessages: paragraph.editedMessages,
      },
    });

    interfaceBridge.nav(
      '개인별 메시지',
      `/teacherSide/post/notePost/${this.postId}/personalMessage?tempDataKey=${tempDataKey}`,
      'current',
    );
  };

  // 승인 후 직전 대기글로 이동
  approve = async () => {
    this.updatePostReviewStatus('approved');
  };

  // 거절 후 직전 대기글로 이동
  reject = async () => {
    this.updatePostReviewStatus('rejected');
  };

  // 그냥 직전 대기글로 이동
  next = async () => {
    this.updatePostReviewStatus('pending');
  };

  openNextPost = (postType: PostTypeT, postId: number) => {
    // this.init(this.scope, postType, postId);
    console.log('openNextPost');
    const newUrl = `/teacherSide/post/review/${this.scope}/${postType}/${postId}`;
    navigate(newUrl, { replace: true });
  };

  updatePostReviewStatus = async (newStatus: PostReviewStatusT) => {
    if (!this.post) return;
    if (this.scope !== 'inbox') return;

    try {
      const result = await api<
        NewStatusRequestT<PostReviewStatusT>,
        MyResponse<PostReviewStatusUpdateResponseT>
      >(DO_UPDATE_POST_REVIEW_STATUS(0, this.post.type, this.post.id), {
        newStatus,
      });

      if (result.isSuccess && result.object) {
        const nextPost = result.object.nextInboxPost;
        if (nextPost) {
          this.openNextPost(nextPost.type, nextPost.id);
        } else {
          await interfaceBridge.customAlert(
            '검토 완료',
            '모든 글을 검토했습니다.',
            '닫기',
          );
          this.goToList();
        }
      } else if (result.errorCode === ErrorCode.POST_REVIEW_ALREADY_PUBLISHED) {
        await interfaceBridge.customAlert(
          '승인/반려 실패',
          '이미 발행된 글입니다.',
          '닫기',
        );

        // 이미 삭제
        // 누가 이미 승인
      } else if (!result.isSuccess) {
        await interfaceBridge.customAlert(
          '승인/반려 실패',
          `오류가 발생했습니다. (${result.errorCode})`,
          '닫기',
        );
      }
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
  };
}
